import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
	AlarmClock,
	AlertTriangle,
	ArrowDown,
	ArrowUp,
	Camera,
	Car,
	CheckCircle,
	Circle,
	CreditCard,
	DoorClosed,
	EyeOff,
	FileMinus,
	FileWarning,
	Loader2,
	Printer,
	Radar,
	RotateCcw,
	Scan,
	ShieldCheck,
	Siren,
	Sparkles,
	VideoOff,
	Waves,
	WifiOff
} from 'lucide-react';

import { usePageHeader } from '@/contexts/PageHeaderContext';
import { Badge } from '@/components/ui/badge';

const DEVICE_UPDATE_INTERVAL = 3600;

const formatRelativeTime = (
	timestamp: string,
	translate: ReturnType<typeof useTranslation>['t']
) => {
	const diffMs = Date.now() - new Date(timestamp).getTime();
	const diffMinutes = Math.floor(diffMs / 60000);

	if (diffMinutes < 1) return translate('gateMonitor:time.justNow');
	if (diffMinutes === 1) return translate('gateMonitor:time.minute');
	if (diffMinutes < 60)
		return translate('gateMonitor:time.minutes', { count: diffMinutes });

	const diffHours = Math.floor(diffMinutes / 60);
	if (diffHours === 1) return translate('gateMonitor:time.hour');

	return translate('gateMonitor:time.hours', { count: diffHours });
};

type GateType = 'entrance' | 'exit' | 'paycenter';
type GateHealth = 'healthy' | 'degraded' | 'offline';

type DeviceType =
	| 'loopBefore'
	| 'loopAfter'
	| 'barrier'
	| 'printer'
	| 'pos'
	| 'camera';

type DeviceStatus =
	| 'vehicleOn'
	| 'vehicleOff'
	| 'lifted'
	| 'lowered'
	| 'ready'
	| 'offline'
	| 'error'
	| 'paperLow'
	| 'paperOut'
	| 'jammed'
	| 'idle'
	| 'detecting'
	| 'captured';

const gateTranslationKeys: Record<string, string> = {
	'north-entry': 'northEntry',
	'south-exit': 'southExit',
	'west-exit': 'westExit',
	'lobby-paycenter': 'lobbyPaycenter'
};

interface GateDevice {
	id: string;
	name: string;
	type: DeviceType;
	status: DeviceStatus;
	lastUpdated: string;
}

interface GateState {
	id: string;
	name: string;
	type: GateType;
	health: GateHealth;
	lastEvent: string;
	lastUpdated: string;
	queue: number;
	flowRate: number;
	devices: GateDevice[];
}

const deviceIcons: Record<
	DeviceType,
	React.ComponentType<{ className?: string }>
> = {
	loopBefore: Waves,
	loopAfter: Radar,
	barrier: DoorClosed,
	printer: AlarmClock,
	pos: CreditCard,
	camera: Camera
};

const deviceStatusTone: Partial<
	Record<DeviceStatus, 'good' | 'warn' | 'critical'>
> = {
	vehicleOn: 'warn',
	vehicleOff: 'good',
	lifted: 'warn',
	lowered: 'good',
	ready: 'good',
	offline: 'critical',
	error: 'critical',
	paperLow: 'warn',
	paperOut: 'critical',
	jammed: 'critical',
	idle: 'good',
	detecting: 'warn',
	captured: 'good'
};

const deviceStatusIcons: Record<
	DeviceType,
	Partial<Record<DeviceStatus, ComponentType<{ className?: string }>>>
> = {
	loopBefore: {
		vehicleOn: Car,
		vehicleOff: Circle
	},
	loopAfter: {
		vehicleOn: Car,
		vehicleOff: Circle
	},
	barrier: {
		lifted: ArrowUp,
		lowered: ArrowDown
	},
	printer: {
		ready: Printer,
		paperLow: FileWarning,
		paperOut: FileMinus,
		jammed: RotateCcw
	},
	pos: {
		ready: CheckCircle,
		offline: WifiOff,
		error: AlertTriangle
	},
	camera: {
		idle: EyeOff,
		detecting: Scan,
		captured: Camera,
		offline: VideoOff,
		error: AlertTriangle
	}
};

const getHealthTone = (health: GateHealth) => {
	switch (health) {
		case 'healthy':
			return 'text-green-600 dark:text-green-400 bg-green-100/80 dark:bg-green-900/40 border-green-200/80 dark:border-green-800';
		case 'degraded':
			return 'text-amber-600 dark:text-amber-300 bg-amber-100/80 dark:bg-amber-900/30 border-amber-200/70 dark:border-amber-800';
		default:
			return 'text-rose-500 dark:text-rose-300 bg-rose-100/80 dark:bg-rose-900/30 border-rose-200/70 dark:border-rose-800';
	}
};

const toneClass = (tone: 'good' | 'warn' | 'critical' | undefined) => {
	switch (tone) {
		case 'good':
			return 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800';
		case 'warn':
			return 'bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800';
		case 'critical':
			return 'bg-rose-50 text-rose-700 border-rose-100 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800';
		default:
			return 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/40 dark:text-slate-200 dark:border-slate-700';
	}
};

const deviceStatusKey: Record<
	DeviceType,
	Partial<Record<DeviceStatus, string>>
> = {
	loopBefore: { vehicleOn: 'vehicleOn', vehicleOff: 'vehicleOff' },
	loopAfter: { vehicleOn: 'vehicleOn', vehicleOff: 'vehicleOff' },
	barrier: { lifted: 'lifted', lowered: 'lowered' },
	printer: {
		ready: 'ready',
		paperLow: 'paperLow',
		paperOut: 'paperOut',
		jammed: 'jammed'
	},
	pos: { ready: 'ready', offline: 'offline', error: 'error' },
	camera: {
		idle: 'idle',
		detecting: 'detecting',
		captured: 'captured',
		offline: 'offline',
		error: 'error'
	}
};

const formatTimestamp = (options?: { minutesAgo?: number; iso?: string }) => {
	if (options?.iso) {
		return new Date(options.iso).toISOString();
	}

	const offset = (options?.minutesAgo ?? 0) * 60 * 1000;
	return new Date(Date.now() - offset).toISOString();
};

const randomOf = <T,>(values: T[]): T =>
	values[Math.floor(Math.random() * values.length)];

const updateDeviceState = (
	device: GateDevice,
	type: GateType,
	translate: (key: string, options?: Record<string, unknown>) => string
): { device: GateDevice; event: string; severity: GateHealth } => {
	let nextStatus: DeviceStatus = device.status;
	const now = new Date().toISOString();

	switch (device.type) {
		case 'loopBefore':
		case 'loopAfter':
			nextStatus = Math.random() > 0.55 ? 'vehicleOn' : 'vehicleOff';
			break;
		case 'barrier':
			nextStatus = Math.random() > 0.6 ? 'lifted' : 'lowered';
			break;
		case 'printer': {
			const options: DeviceStatus[] = [
				'ready',
				'paperLow',
				'paperOut',
				'jammed'
			];
			nextStatus = randomOf(options);
			break;
		}
		case 'pos':
			nextStatus = randomOf(['ready', 'offline', 'error']);
			break;
		case 'camera':
			nextStatus = randomOf(['idle', 'detecting', 'captured', 'offline']);
			break;
	}

	const severity: GateHealth =
		nextStatus === 'offline' || nextStatus === 'error'
			? 'offline'
			: nextStatus === 'jammed' || nextStatus === 'paperOut'
				? 'degraded'
				: 'healthy';

	const stateKey = deviceStatusKey[device.type][nextStatus] || 'generic';

	const event = translate('gateMonitor:events.deviceUpdate', {
		device: translate(`gateMonitor:devices.${device.type}.label`),
		status: translate(
			`gateMonitor:devices.${device.type}.states.${stateKey}`
		),
		gateType: translate(`gateMonitor:gate.types.${type}`)
	});

	return {
		device: { ...device, status: nextStatus, lastUpdated: now },
		event,
		severity
	};
};

const createInitialGates = (t: (key: string) => string): GateState[] => {
	const now = formatTimestamp();
	const fiveMinutesAgo = formatTimestamp({ minutesAgo: 5 });
	const tenMinutesAgo = formatTimestamp({ minutesAgo: 10 });
	const thirtyMinutesAgo = formatTimestamp({ minutesAgo: 30 });
	const sundayLate = formatTimestamp({ iso: '2025-08-10T23:40:00Z' });
	const aug12Morning = formatTimestamp({ iso: '2025-08-12T08:35:00Z' });
	const aug12Evening = formatTimestamp({ iso: '2025-08-12T19:17:00Z' });

	return [
		{
			id: 'north-entry',
			name: t('gateMonitor:gates.northEntry'),
			type: 'entrance',
			health: 'healthy',
			lastEvent: t('gateMonitor:events.startedMonitoring'),
			lastUpdated: fiveMinutesAgo,
			queue: 2,
			flowRate: 74,
			devices: [
				{
					id: 'ne-loop-1',
					name: t('gateMonitor:devices.loopBefore.label'),
					type: 'loopBefore',
					status: 'vehicleOff',
					lastUpdated: fiveMinutesAgo
				},
				{
					id: 'ne-loop-2',
					name: t('gateMonitor:devices.loopAfter.label'),
					type: 'loopAfter',
					status: 'vehicleOff',
					lastUpdated: tenMinutesAgo
				},
				{
					id: 'ne-barrier',
					name: t('gateMonitor:devices.barrier.label'),
					type: 'barrier',
					status: 'lowered',
					lastUpdated: now
				},
				{
					id: 'ne-pos',
					name: t('gateMonitor:devices.pos.label'),
					type: 'pos',
					status: 'ready',
					lastUpdated: tenMinutesAgo
				},
				{
					id: 'ne-printer',
					name: t('gateMonitor:devices.printer.label'),
					type: 'printer',
					status: 'ready',
					lastUpdated: sundayLate
				},
				{
					id: 'ne-camera',
					name: t('gateMonitor:devices.camera.label'),
					type: 'camera',
					status: 'idle',
					lastUpdated: aug12Morning
				}
			]
		},
		{
			id: 'south-exit',
			name: t('gateMonitor:gates.southExit'),
			type: 'exit',
			health: 'healthy',
			lastEvent: t('gateMonitor:events.startedMonitoring'),
			lastUpdated: tenMinutesAgo,
			queue: 0,
			flowRate: 58,
			devices: [
				{
					id: 'se-loop-1',
					name: t('gateMonitor:devices.loopBefore.label'),
					type: 'loopBefore',
					status: 'vehicleOff',
					lastUpdated: tenMinutesAgo
				},
				{
					id: 'se-loop-2',
					name: t('gateMonitor:devices.loopAfter.label'),
					type: 'loopAfter',
					status: 'vehicleOff',
					lastUpdated: fiveMinutesAgo
				},
				{
					id: 'se-barrier',
					name: t('gateMonitor:devices.barrier.label'),
					type: 'barrier',
					status: 'lowered',
					lastUpdated: aug12Evening
				},
				{
					id: 'se-camera',
					name: t('gateMonitor:devices.camera.label'),
					type: 'camera',
					status: 'idle',
					lastUpdated: sundayLate
				}
			]
		},
		{
			id: 'west-exit',
			name: t('gateMonitor:gates.westExit'),
			type: 'exit',
			health: 'degraded',
			lastEvent: t('gateMonitor:events.startedMonitoring'),
			lastUpdated: thirtyMinutesAgo,
			queue: 5,
			flowRate: 46,
			devices: [
				{
					id: 'we-loop-1',
					name: t('gateMonitor:devices.loopBefore.label'),
					type: 'loopBefore',
					status: 'vehicleOff',
					lastUpdated: thirtyMinutesAgo
				},
				{
					id: 'we-barrier',
					name: t('gateMonitor:devices.barrier.label'),
					type: 'barrier',
					status: 'lifted',
					lastUpdated: fiveMinutesAgo
				},
				{
					id: 'we-pos',
					name: t('gateMonitor:devices.pos.label'),
					type: 'pos',
					status: 'ready',
					lastUpdated: now
				},
				{
					id: 'we-printer',
					name: t('gateMonitor:devices.printer.label'),
					type: 'printer',
					status: 'paperLow',
					lastUpdated: aug12Evening
				}
			]
		},
		{
			id: 'lobby-paycenter',
			name: t('gateMonitor:gates.lobbyPaycenter'),
			type: 'paycenter',
			health: 'healthy',
			lastEvent: t('gateMonitor:events.startedMonitoring'),
			lastUpdated: aug12Morning,
			queue: 1,
			flowRate: 92,
			devices: [
				{
					id: 'pc-pos',
					name: t('gateMonitor:devices.pos.label'),
					type: 'pos',
					status: 'ready',
					lastUpdated: aug12Morning
				},
				{
					id: 'pc-printer',
					name: t('gateMonitor:devices.printer.label'),
					type: 'printer',
					status: 'ready',
					lastUpdated: aug12Evening
				}
			]
		}
	];
};

const LivePill = ({ label }: { label: string }) => (
	<div className="inline-flex items-center gap-2 rounded-full px-3 py-1 bg-emerald-500/10 text-emerald-600 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-200 dark:border-emerald-800">
		<motion.span
			className="w-2 h-2 rounded-full bg-emerald-500"
			animate={{
				scale: [1, 1.25, 1],
				opacity: [0.8, 1, 0.8]
			}}
			transition={{ repeat: Infinity, duration: 1.6 }}
		/>
		<span className="text-xs font-semibold uppercase tracking-wide">
			{label}
		</span>
	</div>
);

const FlowMeter = ({ value, label }: { value: number; label: string }) => (
	<div className="space-y-1">
		<div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
			<span>{label}</span>
			<span className="font-semibold text-slate-700 dark:text-slate-200">
				{value}%
			</span>
		</div>
		<div className="h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
			<motion.div
				className="h-full rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"
				initial={{ width: 0 }}
				animate={{ width: `${value}%` }}
				transition={{ type: 'spring', stiffness: 120, damping: 20 }}
			/>
		</div>
	</div>
);

const DeviceCard = ({
	device,
	gateType,
	translation
}: {
	device: GateDevice;
	gateType: GateType;
	translation: ReturnType<typeof useTranslation>['t'];
}) => {
	const TypeIcon = deviceIcons[device.type];
	const StatusIcon =
		deviceStatusIcons[device.type]?.[device.status] ?? TypeIcon;
	const statusKey = deviceStatusKey[device.type][device.status] || 'generic';
	const statusLabel = translation(
		`gateMonitor:devices.${device.type}.states.${statusKey}`
	);
	const tone = toneClass(deviceStatusTone[device.status]);

	return (
		<motion.div
			layout
			className={`border rounded-xl p-3 bg-white/60 dark:bg-slate-900/40 shadow-sm transition-all duration-300 ${tone}`}
		>
			<div className="flex items-start gap-3">
				<div className="p-2 rounded-lg bg-white/80 dark:bg-slate-900 shadow-inner">
					<TypeIcon className="w-5 h-5" />
				</div>
				<div className="flex-1 space-y-1">
					<div className="flex items-center justify-between">
						<p className="text-sm font-semibold text-slate-900 dark:text-slate-50">
							{device.name}
						</p>
						<Badge variant="outline" className="text-[10px]">
							{translation(`gateMonitor:gate.types.${gateType}`)}
						</Badge>
					</div>
				</div>
			</div>
			<div className="mt-3 flex items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-300">
				<span className="inline-flex items-center gap-2 rounded-full bg-black/5 dark:bg-white/5 px-2 py-1">
					<StatusIcon className="w-3 h-3" />
					<span>{statusLabel}</span>
				</span>
				<span className="text-right text-[10px] opacity-80">
					{translation('gateMonitor:lastUpdate', {
						time: formatRelativeTime(
							device.lastUpdated,
							translation
						)
					})}
				</span>
			</div>
		</motion.div>
	);
};

const GateCard = ({
	gate,
	t
}: {
	gate: GateState;
	t: ReturnType<typeof useTranslation>['t'];
}) => {
	const TypeIcon =
		gate.type === 'paycenter'
			? Sparkles
			: gate.type === 'exit'
				? ShieldCheck
				: Siren;
	const healthClass = getHealthTone(gate.health);

	return (
		<motion.div
			layout
			className="sentry-card border border-slate-200/60 dark:border-slate-800/80 shadow-sm"
			transition={{ type: 'spring', stiffness: 120, damping: 18 }}
		>
			<div className="sentry-card-header pb-2">
				<div className="flex items-start justify-between gap-4">
					<div className="flex items-center gap-3">
						<div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:bg-blue-500/15 dark:text-blue-200">
							<TypeIcon className="w-5 h-5" />
						</div>
						<div>
							<h3 className="text-lg font-semibold text-slate-900 dark:text-white">
								{gate.name}
							</h3>
							<p className="text-xs text-slate-500 dark:text-slate-400">
								{t(`gateMonitor:gate.types.${gate.type}`)}
							</p>
						</div>
					</div>
					<div className="flex items-center gap-2">
						<span
							className={`text-xs font-semibold px-3 py-1 rounded-full border ${healthClass}`}
						>
							{t(`gateMonitor:health.${gate.health}`)}
						</span>
						<Badge
							variant="secondary"
							className="bg-slate-100 dark:bg-slate-800"
						>
							{t('gateMonitor:gate.queue', { count: gate.queue })}
						</Badge>
					</div>
				</div>
			</div>

			<div className="sentry-card-body space-y-4">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
					<FlowMeter
						value={gate.flowRate}
						label={t('gateMonitor:metrics.flowRate')}
					/>
					<FlowMeter
						value={Math.min(100, gate.queue * 18)}
						label={t('gateMonitor:metrics.queueLoad')}
					/>
				</div>

				<div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 px-4 py-3 bg-slate-100 dark:bg-slate-900/60">
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
						<div>
							<p className="text-[11px] tracking-wide uppercase text-slate-500 dark:text-slate-400">
								{t('gateMonitor:gate.lastEvent')}
							</p>
							<p className="text-sm font-semibold text-slate-900 dark:text-white">
								{gate.lastEvent}
							</p>
						</div>
						<span className="text-[11px] text-slate-500 dark:text-slate-400">
							{t('gateMonitor:lastUpdate', {
								time: formatRelativeTime(gate.lastUpdated, t)
							})}
						</span>
					</div>
				</div>

				<div className="grid gap-3 sm:grid-cols-2">
					<AnimatePresence initial={false}>
						{gate.devices.map(device => (
							<DeviceCard
								key={device.id}
								device={device}
								gateType={gate.type}
								translation={t}
							/>
						))}
					</AnimatePresence>
				</div>
			</div>
		</motion.div>
	);
};

export const GateMonitor = () => {
	const { t } = useTranslation(['gateMonitor', 'common']);
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const [gates, setGates] = useState<GateState[]>(() =>
		createInitialGates(t)
	);

	useEffect(() => {
		setPageHeader({
			title: t('gateMonitor:title'),
			description: t('gateMonitor:subtitle')
		});

		return () => {
			resetPageHeader();
		};
	}, [resetPageHeader, setPageHeader, t]);

	useEffect(() => {
		setGates(prev =>
			prev.map(gate => ({
				...gate,
				name: t(
					`gateMonitor:gates.${
						gateTranslationKeys[gate.id] ?? gate.id
					}`
				),
				devices: gate.devices.map(device => ({
					...device,
					name: t(`gateMonitor:devices.${device.type}.label`)
				}))
			}))
		);
	}, [t]);

	useEffect(() => {
		const interval = setInterval(() => {
			setGates(prev =>
				prev.map(gate => {
					const updatedDevices = gate.devices.map(device => {
						if (Math.random() > 0.5) {
							const {
								device: nextDevice,
								event,
								severity
							} = updateDeviceState(device, gate.type, t);

							return {
								...nextDevice,
								gateEvent: event,
								gateSeverity: severity
							} as GateDevice & {
								gateEvent: string;
								gateSeverity: GateHealth;
							};
						}

						return device;
					});

					const criticalDevice = updatedDevices.find(
						d =>
							(d as GateDevice & { gateSeverity?: GateHealth })
								.gateSeverity === 'offline'
					);
					const degradedDevice = updatedDevices.find(
						d =>
							(d as GateDevice & { gateSeverity?: GateHealth })
								.gateSeverity === 'degraded'
					);
					const lastEvent =
						(
							updatedDevices.find(
								d =>
									(d as GateDevice & { gateEvent?: string })
										.gateEvent
							) as GateDevice & { gateEvent?: string }
						)?.gateEvent || gate.lastEvent;

					return {
						...gate,
						devices: updatedDevices.map(d => ({
							id: d.id,
							name: d.name,
							status: d.status,
							type: d.type,
							lastUpdated: d.lastUpdated
						})),
						lastEvent,
						health: criticalDevice
							? 'offline'
							: degradedDevice
								? 'degraded'
								: 'healthy',
						lastUpdated: new Date().toISOString(),
						queue: Math.max(
							0,
							Math.min(
								12,
								gate.queue + (Math.random() > 0.5 ? 1 : -1)
							)
						),
						flowRate: Math.max(
							18,
							Math.min(
								98,
								gate.flowRate + (Math.random() > 0.5 ? 5 : -7)
							)
						)
					};
				})
			);
		}, DEVICE_UPDATE_INTERVAL);

		return () => clearInterval(interval);
	}, [t]);

	const stats = useMemo(() => {
		const healthy = gates.filter(g => g.health === 'healthy').length;
		const degraded = gates.filter(g => g.health === 'degraded').length;
		const offline = gates.filter(g => g.health === 'offline').length;
		return { healthy, degraded, offline };
	}, [gates]);

	return (
		<div className="p-4 lg:p-6 space-y-6">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="space-y-1">
					<div className="flex items-center gap-2">
						<LivePill label={t('gateMonitor:live')} />
						<span className="text-xs text-slate-500 dark:text-slate-400">
							{t('gateMonitor:refreshHint')}
						</span>
					</div>
					<p className="text-sm text-slate-500 dark:text-slate-300">
						{t('gateMonitor:description')}
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Badge className="bg-emerald-500/10 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800">
						{t('gateMonitor:totals.healthy', {
							count: stats.healthy
						})}
					</Badge>
					<Badge className="bg-amber-500/10 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200 border-amber-200 dark:border-amber-800">
						{t('gateMonitor:totals.degraded', {
							count: stats.degraded
						})}
					</Badge>
					<Badge className="bg-rose-500/10 text-rose-700 dark:bg-rose-500/10 dark:text-rose-200 border-rose-200 dark:border-rose-800">
						{t('gateMonitor:totals.offline', {
							count: stats.offline
						})}
					</Badge>
				</div>
			</div>

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
				{gates.map(gate => (
					<GateCard key={gate.id} gate={gate} t={t} />
				))}
			</div>

			<div className="sentry-card border border-slate-200/60 dark:border-slate-800/80">
				<div className="sentry-card-body flex items-center gap-3 text-sm text-slate-600 dark:text-slate-300">
					<Loader2 className="w-4 h-4 animate-spin" />
					<span>{t('gateMonitor:simulationNotice')}</span>
				</div>
			</div>
		</div>
	);
};

export default GateMonitor;
